import { useCallback, useEffect, useRef } from 'react';
import { signOut } from 'firebase/auth';
import { toast } from 'sonner';
import { auth } from '../firebase';
import {
  ACTIVITY_THROTTLE_MS,
  IDLE_TIMEOUT_MS,
  IDLE_WARNING_MS,
  IDLE_WARNING_TOAST_ID,
  LAST_ACTIVITY_AT_KEY,
  readLastActivityAt,
  setLastActivityAt,
} from '../constants/sessionTimeout';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];

function dismissIdleWarning() {
  toast.dismiss(IDLE_WARNING_TOAST_ID);
}

function showIdleWarning() {
  toast.warning('You will be signed out in 5 minutes due to inactivity.', {
    id: IDLE_WARNING_TOAST_ID,
    duration: Infinity,
  });
}

/**
 * Signs the user out after {@link IDLE_TIMEOUT_MS} of no user input.
 * Shows a warning {@link IDLE_WARNING_MS} before logout.
 */
export function useIdleSessionTimeout(user) {
  const localActivityRef = useRef(Date.now());
  const lastWriteRef = useRef(0);
  const timeoutRef = useRef(null);
  const warningShownRef = useRef(false);
  const loggingOutRef = useRef(false);

  const getLastActivityAt = useCallback(() => {
    const stored = readLastActivityAt();
    const storedValid = stored ?? 0;
    return Math.max(storedValid, localActivityRef.current);
  }, []);

  const getRemainingMs = useCallback(() => {
    return Math.max(0, IDLE_TIMEOUT_MS - (Date.now() - getLastActivityAt()));
  }, [getLastActivityAt]);

  const scheduleLogoutCheck = useCallback((checkExpiry) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(checkExpiry, getRemainingMs());
  }, [getRemainingMs]);

  useEffect(() => {
    if (!user?.uid) return undefined;

    loggingOutRef.current = false;
    warningShownRef.current = false;

    if (readLastActivityAt() === null) {
      setLastActivityAt();
    }
    localActivityRef.current = getLastActivityAt();

    const expireSession = async () => {
      if (loggingOutRef.current) return;
      loggingOutRef.current = true;
      dismissIdleWarning();
      toast.info('You were signed out after 5 hours of inactivity.');
      try {
        await signOut(auth);
      } catch (err) {
        console.error('Idle logout failed:', err);
        loggingOutRef.current = false;
      }
    };

    const checkExpiry = () => {
      const remaining = getRemainingMs();

      if (remaining <= 0) {
        expireSession();
        return;
      }

      if (remaining <= IDLE_WARNING_MS) {
        if (!warningShownRef.current) {
          warningShownRef.current = true;
          showIdleWarning();
        }
      } else {
        warningShownRef.current = false;
        dismissIdleWarning();
      }

      scheduleLogoutCheck(checkExpiry);
    };

    const recordActivity = () => {
      const now = Date.now();
      localActivityRef.current = now;

      if (now - lastWriteRef.current >= ACTIVITY_THROTTLE_MS) {
        lastWriteRef.current = now;
        setLastActivityAt(now);
        warningShownRef.current = false;
        dismissIdleWarning();
      }

      scheduleLogoutCheck(checkExpiry);
    };

    const onStorage = (event) => {
      if (event.key !== LAST_ACTIVITY_AT_KEY) return;
      const stored = readLastActivityAt();
      if (stored !== null) {
        localActivityRef.current = Math.max(localActivityRef.current, stored);
      }
      warningShownRef.current = false;
      dismissIdleWarning();
      scheduleLogoutCheck(checkExpiry);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkExpiry();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);

    const intervalId = window.setInterval(checkExpiry, 60_000);
    checkExpiry();

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, recordActivity);
      });
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
      clearInterval(intervalId);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      dismissIdleWarning();
    };
  }, [user?.uid, getLastActivityAt, getRemainingMs, scheduleLogoutCheck]);
}
